import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';

import { api, imageUrl, itemImageUrl } from '../../api/client';
import type { LibraryItem } from '../../api/types';
import { useApi } from '../../api/useApi';
import { easing } from '../../theme';
import { episodeCode } from './playbackTime';
import { TileProgress } from './WatchProgress';

/// Where there is no artwork, a card has to carry the item on its own. The tile takes its
/// colour from the title, so a shelf of undescribed items reads as a shelf of different things
/// rather than a wall of identical placeholders. The initial on it is the page's own text
/// colour, so the tile is deep in a dark interface and pale in a light one.
function tint(title: string, mode: 'light' | 'dark'): string {
  let hash = 0;

  for (let index = 0; index < title.length; index += 1) {
    hash = (hash * 31 + title.charCodeAt(index)) % 360;
  }

  const [from, to] = mode === 'dark' ? [26, 14] : [82, 72];

  return `linear-gradient(150deg, hsl(${hash} 42% ${from}%) 0%, hsl(${(hash + 40) % 360} 38% ${to}%) 100%)`;
}

interface MediaCardProps {
  item: LibraryItem;
  /// Episodes are wider than they are tall, like the frame they come from.
  aspectRatio?: string;
  subtitle?: string;
  /// On a shelf beside films, an episode is shown as its programme: the series' poster and
  /// name, at the size and shape of a film, with the episode itself on the line below.
  asProgramme?: boolean;
}

export default function MediaCard({ item, aspectRatio, subtitle, asProgramme = false }: MediaCardProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const programme = asProgramme && item.kind === 'Episode';
  const ratio = aspectRatio ?? (item.kind === 'Episode' && !programme ? '16 / 9' : '2 / 3');
  const poster = programme && item.seriesImage ? imageUrl(item.seriesImage) : itemImageUrl(item);
  const title = programme ? item.seriesTitle ?? item.title : item.title;

  // One request for the whole screen: every card asks for the same thing, and the cache
  // answers all but the first.
  const progress = useApi(['playback', 'progress'], api.playbackProgress);
  const started = progress.data?.[item.id];

  return (
    <Box
      component="button"
      onClick={() => navigate(`/items/${item.id}`)}
      sx={{
        appearance: 'none',
        border: 0,
        padding: 0,
        background: 'none',
        color: 'inherit',
        cursor: 'pointer',
        textAlign: 'start',
        display: 'block',
        width: '100%',
        outline: 'none',

        // The focus ring of a television: the tile comes forward and a ring stands off it,
        // so the one that is chosen is unmistakable from across a room.
        '&:hover .tile, &:focus-visible .tile': {
          transform: 'translateY(-4px) scale(1.035)',
          outlineColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.92)' : theme.palette.primary.main,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 26px 50px -18px rgba(0,0,0,0.95), 0 0 40px -12px rgba(19,216,210,0.35)'
            : '0 22px 40px -18px rgba(16,19,25,0.45)',
        },
        '&:hover .shine, &:focus-visible .shine': { opacity: 1 },
        '&:hover .caption, &:focus-visible .caption': { color: 'text.primary' },
      }}
    >
      <Box
        className="tile"
        sx={{
          aspectRatio: ratio,
          borderRadius: '14px',
          background: tint(title, theme.palette.mode),
          outline: '2px solid transparent',
          outlineOffset: 3,
          display: 'grid',
          placeItems: 'center',
          transition: `transform 500ms ${easing.out}, box-shadow 400ms ease, outline-color 200ms ease`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 14px 30px -18px rgba(0,0,0,0.9)'
            : '0 10px 24px -16px rgba(16,19,25,0.35)',
          overflow: 'hidden',
          position: 'relative',

          // A hairline drawn inside the picture, so a poster with a pale edge still has one.
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            boxShadow: `inset 0 0 0 1px ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(16,19,25,0.08)'}`,
            pointerEvents: 'none',
          },
        }}
      >
        {poster ? (
          <Box
            component="img"
            src={poster}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: 44,
              fontWeight: 700,
              opacity: 0.55,
              letterSpacing: '-0.03em',
              userSelect: 'none',
            }}
          >
            {title.slice(0, 1).toUpperCase()}
          </Typography>
        )}

        {/* Glass catching the light as the tile comes forward. */}
        <Box
          className="shine"
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 40%)',
            opacity: 0,
            transition: 'opacity 300ms ease',
            pointerEvents: 'none',
          }}
        />

        {started && started.fraction > 0 && <TileProgress fraction={started.fraction} />}
      </Box>

      <Typography
        className="caption"
        variant="subtitle2"
        sx={{ mt: 1.25, lineHeight: 1.3, fontWeight: 650, color: 'text.primary', transition: 'color 200ms ease' }}
        noWrap
      >
        {title}
      </Typography>

      {/* The line stays even when there is nothing to put on it. A film without a year would
          otherwise make its tile shorter than the one beside it, and a shelf of tiles that do
          not line up looks broken rather than sparse. */}
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ display: 'block', minHeight: '1.25rem' }}
      >
        {subtitle ?? (programme ? programmeSubtitle(item) : defaultSubtitle(item))}
      </Typography>
    </Box>
  );
}

/// Which episode, when the tile itself is the series.
function programmeSubtitle(item: LibraryItem): string {
  const number = defaultSubtitle(item);

  return item.seriesTitle && number ? `${number} · ${item.title}` : number || item.title;
}

function defaultSubtitle(item: LibraryItem): string {
  if (item.kind === 'Episode' && item.seasonNumber !== null && item.episodeNumber !== null) {
    return episodeCode(item.seasonNumber, item.episodeNumber);
  }

  return item.year?.toString() ?? '';
}
